import type { PrintProviderProfile, ProductProfile } from './schemas.js';
import { CURRENT_SCHEMA_VERSION } from './constants.js';

/** Demo spine formula — clearly fictional, not for real print partners */
export const DEMO_SPINE_FORMULA = {
  id: 'demo-spine-v1',
  label: 'Demonstratie-rugformule (niet voor productie)',
  disclaimer:
    'Deze rugberekening is fictief en uitsluitend bedoeld voor demonstratie. Een echte drukker levert een eigen formule.',
  baseMm: 1.5,
  mmPerPage: 0.085,
  paperFactors: {
    'standard-matte': 1,
    'premium-gloss': 1.12,
    layflat: 1.35,
  },
} as const;

export const SQUARE_200_PRODUCT: ProductProfile = {
  schemaVersion: CURRENT_SCHEMA_VERSION,
  id: 'square-200-matte',
  name: 'Vierkant fotoboek 200 × 200 mm',
  description: 'Standaard vierkant binnenwerk met losse voor- en achterkaft plus rug.',
  pageSizeMm: { width: 200, height: 200 },
  bleedMm: 3,
  safeMarginMm: 8,
  pageCount: {
    min: 24,
    max: 120,
    default: 24,
    step: 2,
  },
  paperStocks: [
    {
      id: 'standard-matte',
      name: 'Standaard mat',
      description: 'Demo-papiersoort voor standaardboeken.',
    },
    {
      id: 'premium-gloss',
      name: 'Premium glans',
      description: 'Demo-papiersoort met iets dikkere rugfactor.',
    },
    {
      id: 'layflat',
      name: 'Layflat',
      description: 'Demo-papiersoort voor layflat-binding.',
    },
  ],
  spineFormula: { ...DEMO_SPINE_FORMULA, paperFactors: { ...DEMO_SPINE_FORMULA.paperFactors } },
  targetDpi: 300,
  warnDpi: 200,
  minDpi: 120,
  colorProfile: 'sRGB',
  cover: {
    hasFront: true,
    hasBack: true,
    hasSpine: true,
    wrapBleedMm: 3,
  },
  export: {
    defaultDpi: 300,
    jpegQuality: 0.92,
    includeBleed: true,
  },
};

export const PRODUCT_PROFILES: Record<string, ProductProfile> = {
  [SQUARE_200_PRODUCT.id]: SQUARE_200_PRODUCT,
};

/**
 * Seed stub for a future real print-partner integration. `status: 'stub'`
 * is load-bearing: nothing in this codebase treats a print provider as
 * orderable, and no ordering/checkout UI is wired to it. It exists purely
 * so the domain model and docs describing "adapter hook for future print
 * partners" have a concrete, honest example instead of dangling claims.
 */
export const DEMO_PRINT_PROVIDER: PrintProviderProfile = {
  schemaVersion: CURRENT_SCHEMA_VERSION,
  id: 'demo-print-provider',
  name: 'Demo drukkerij (niet actief — stub)',
  status: 'stub',
  productProfileId: SQUARE_200_PRODUCT.id,
};

export const PRINT_PROVIDER_PROFILES: Record<string, PrintProviderProfile> = {
  [DEMO_PRINT_PROVIDER.id]: DEMO_PRINT_PROVIDER,
};

export function listPrintProviderProfiles(): PrintProviderProfile[] {
  return Object.values(PRINT_PROVIDER_PROFILES);
}

export function getProductProfile(id: string): ProductProfile {
  const profile = PRODUCT_PROFILES[id];
  if (!profile) {
    throw new Error(`Onbekend productprofiel: ${id}`);
  }
  return profile;
}

export function listProductProfiles(): ProductProfile[] {
  return Object.values(PRODUCT_PROFILES);
}

export function calculateSpineWidthMm(
  pageCount: number,
  paperStockId: string,
  profile: ProductProfile = SQUARE_200_PRODUCT,
): number {
  const factor = profile.spineFormula.paperFactors[paperStockId] ?? 1;
  return profile.spineFormula.baseMm + pageCount * profile.spineFormula.mmPerPage * factor;
}

export function assertValidPageCount(pageCount: number, profile: ProductProfile): void {
  const { min, max, step } = profile.pageCount;
  if (pageCount < min || pageCount > max) {
    throw new Error(`Pagina-aantal moet tussen ${min} en ${max} liggen.`);
  }
  if ((pageCount - min) % step !== 0) {
    throw new Error(`Pagina-aantal moet in stappen van ${step} liggen.`);
  }
}
